import { UserManager, WebStorageStateStore, User } from 'oidc-client-ts';

const endpoint = import.meta.env.VITE_CASDOOR_ENDPOINT as string;
const clientId = import.meta.env.VITE_CASDOOR_WEB_CLIENT_ID as string;

let instance: UserManager | null = null;

export type WebOidcAuthMode = 'signIn' | 'signUp';

/** Map Casdoor OIDC authorize URL to login vs signup entry. */
export function resolveCasdoorAuthorizeEndpoint(
  authorizationEndpoint: string,
  authority: string,
  mode: WebOidcAuthMode
): string {
  if (mode !== 'signUp') return authorizationEndpoint;
  if (authorizationEndpoint.includes('/login/oauth/authorize')) {
    return authorizationEndpoint.replace('/login/oauth/authorize', '/signup/oauth/authorize');
  }
  return `${authority.replace(/\/$/, '')}/signup/oauth/authorize`;
}

/**
 * Lazy singleton: `window` is only available in the browser, so the
 * UserManager must not be constructed at module load time (SSR-style
 * tests import this module in Node).
 */
export function getUserManager(): UserManager {
  if (!instance) {
    instance = new UserManager({
      authority: endpoint,
      client_id: clientId,
      redirect_uri: `${window.location.origin}/callback`,
      post_logout_redirect_uri: `${window.location.origin}/`,
      response_type: 'code',
      scope: 'openid profile email offline_access',
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      automaticSilentRenew: true,
      // PKCE is on by default in oidc-client-ts; we don't ship a client secret.
    });
  }
  return instance;
}

/**
 * Start Casdoor OIDC. Sign-up uses Casdoor's `/signup/oauth/authorize`
 * (same PKCE query as login authorize) when the web app has SignUp enabled.
 */
export async function startWebOidcAuth(mode: WebOidcAuthMode = 'signIn'): Promise<void> {
  const um = getUserManager();
  if (mode !== 'signUp') {
    await um.signinRedirect();
    return;
  }

  const metadata = await um.metadataService.getMetadata();
  const signupAuthorize = resolveCasdoorAuthorizeEndpoint(
    metadata.authorization_endpoint ?? '',
    String(endpoint ?? ''),
    'signUp'
  );

  const originalGetAuthorizationEndpoint =
    um.metadataService.getAuthorizationEndpoint.bind(um.metadataService);
  um.metadataService.getAuthorizationEndpoint = async () => signupAuthorize;
  try {
    await um.signinRedirect();
  } finally {
    um.metadataService.getAuthorizationEndpoint = originalGetAuthorizationEndpoint;
  }
}

export async function getOidcAccessToken(): Promise<string | null> {
  const user = await getUserManager().getUser();
  return user?.access_token ?? null;
}

export async function getCurrentOidcUser(): Promise<User | null> {
  return getUserManager().getUser();
}
